import { RepresentativeFeature } from 'yeself-common/typings/guide/LayerCommon';

import { elementDocumentRect, getAmountOfText } from 'yeself-client-common/utils/ElementUtils';
import { isWithinYesElfTag } from 'yeself-client-common/utils/DomUtils';

import { elementBlacklist, getNormalizedSelector } from './bestDerivedCardinalityMatch';

import { BestMatch, ElementScore } from './types';

const ELEMENT = 'element';
const SCORE = 'score';
const AMOUNT_OF_TEXT = 'amountOfText';
const DEPTH = 'depth';
const X = 'x';
const Y = 'y';
const SPAN = 'span';
const ROOT_ELEMENT = 'rootElement';
const TAG_ONLY = 'tagOnly';
const MAIN_FEATURES = [AMOUNT_OF_TEXT, DEPTH];
const POST_FILTERING_FEATURES = [X, Y];

const getXCoordinate = (element: HTMLElement): number => {
    const { x } = elementDocumentRect(element);
    return x;
};

const getYCoordinate = (element: HTMLElement): number => {
    const { y } = elementDocumentRect(element);
    return y;
};

const getDepth = (element: HTMLElement): number => {
    let node = element;
    let depth = 1;

    while (node.parentElement !== null) {
        node = node.parentElement;
        depth += 1;
    }
    return depth;
};

const calculateScore = (value1: number, value2: number) =>
    Math.round(Math.abs(value1 - value2) * 100) / 100;

const propertyRetrievalFunction = {
    [AMOUNT_OF_TEXT]: getAmountOfText,
    [DEPTH]: getDepth,
    [X]: getXCoordinate,
    [Y]: getYCoordinate,
};

const addScores = (
    elementScore: ElementScore,
    representativeFeatures: RepresentativeFeature[]
): ElementScore =>
    representativeFeatures.reduce(
        ({ element, score }, representativeFeature) => {
            const calculatedValue = propertyRetrievalFunction[representativeFeature.featureName](
                elementScore.element
            );
            const newScore = calculateScore(calculatedValue, representativeFeature.value as number);
            return {
                element,
                score: score + newScore,
            };
        },
        {
            element: elementScore.element,
            score: elementScore.score,
        }
    );

const scorePotentialElements = (
    potentialElements: Element[],
    representativeFeatures: RepresentativeFeature[]
): ElementScore[] =>
    Array.from(potentialElements).map((element) =>
        addScores({ [ELEMENT]: element, [SCORE]: 0 }, representativeFeatures)
    );

const getOneLowestElementScore = (elementScores: ElementScore[]): ElementScore =>
    elementScores.reduce((acc, value) => (acc.score < value.score ? acc : value), {
        score: Infinity,
        element: document.createElement(SPAN), // any element can be created at the beginning, but it must be some HTMLelement
    });

const getMoreLowestElementScores = (elementScores: ElementScore[]): ElementScore[] => {
    const lowestScore = Math.min(...elementScores.map(({ score }) => score));
    return elementScores.filter(({ score }) => score === lowestScore);
};

const getElementScores = (
    potentialElements: Element[],
    representativeFeatures: RepresentativeFeature[],
    allowed_features: string[],
    elementType: string
): ElementScore[] => {
    const allowedRepresentativeFeatures = representativeFeatures.filter(
        (feature) =>
            feature.elementType === elementType && allowed_features.includes(feature.featureName)
    );
    return scorePotentialElements(potentialElements, allowedRepresentativeFeatures);
};

const getBestMatch = (
    selector: string,
    representativeFeatures: RepresentativeFeature[],
    best: BestMatch,
    elementType: string
): BestMatch => {
    const potentialElements = selector.trim().length ? document.querySelectorAll(selector) : [];
    if (potentialElements.length) {
        // @mzak: filter out elements that are part of YesElf integrated UI
        const noYesElfPotentialElements = Array.from(potentialElements).filter(
            (element) => isWithinYesElfTag(element) === false
        );
        const elementScores = getElementScores(
            noYesElfPotentialElements,
            representativeFeatures,
            MAIN_FEATURES,
            elementType
        );
        const lowestElementsScores = getMoreLowestElementScores(elementScores);

        const elementsWithLowestScores = lowestElementsScores.map(
            (elementScore) => elementScore.element
        );
        const finalElementScores = getElementScores(
            elementsWithLowestScores,
            representativeFeatures,
            POST_FILTERING_FEATURES,
            elementType
        );
        const { score, element } = getOneLowestElementScore(finalElementScores);

        if (score <= best.score) {
            return {
                [ELEMENT]: element,
                [SCORE]: score,
            };
        }
    }
    return best;
};

export const getElementByCssPath = ({
    path,
    representativeFeatures,
}: {
    path: string;
    representativeFeatures: RepresentativeFeature[];
}): Element | null => {
    let best: BestMatch = { element: null, score: Infinity };

    // element match
    const normalizedElementSelector: string = getNormalizedSelector(path, elementBlacklist);
    best = getBestMatch(normalizedElementSelector, representativeFeatures, best, ELEMENT);

    // root element match
    const tagOnlyFeature = representativeFeatures.find(
        (feature) => feature.elementType === ROOT_ELEMENT && feature.featureName === TAG_ONLY
    );
    const normalizedRootElementSelector: string = tagOnlyFeature
        ? getNormalizedSelector(tagOnlyFeature.value as string, elementBlacklist)
        : '';

    const { element } = getBestMatch(
        normalizedRootElementSelector,
        representativeFeatures,
        best,
        ROOT_ELEMENT
    );

    return element;
};
